import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Modal, Form, Input, Rate, Button, Space, Segmented, Tooltip } from "antd";
import { AudioOutlined, StopOutlined, DeleteOutlined, EditOutlined, BorderOutlined, ColumnWidthOutlined, CameraOutlined, UndoOutlined, ClearOutlined } from "@ant-design/icons";

function dataUrlToBlob(dataUrl) {
  try {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  } catch {
    return null;
  }
}

const TOOL_PEN = "pen";
const TOOL_RECT = "rect";
const TOOL_ARROW = "arrow";

export default function FeedbackBugReports({
  open,
  mode = "feedback", // "feedback" | "bug"
  onClose,
  axiosInstance,
  alertDlg,
  initialScreenshot,
  initialText,
}) {
  const isBug = mode === "bug";
  const [form] = Form.useForm();
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);

  // Screenshot + annotation
  const [baseImageUrl, setBaseImageUrl] = useState(null);
  const [tool, setTool] = useState(TOOL_PEN);
  const [color, setColor] = useState("#ff1744");
  const [thickness, setThickness] = useState(3);
  const [actions, setActions] = useState([]); // committed actions
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentPoints, setCurrentPoints] = useState([]); // for pen
  const [previewEnd, setPreviewEnd] = useState(null); // for rect/arrow
  const baseImgRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Console logs
  const [consoleText, setConsoleText] = useState("");

  useEffect(() => {
    if (!window.__srConsolePatched) {
      try {
        window.__srConsolePatched = true;
        window.__srConsoleLogs = window.__srConsoleLogs || [];
        ["log", "warn", "error", "info"].forEach((lvl) => {
          const orig = console[lvl];
          console[lvl] = (...args) => {
            try {
              const ts = new Date().toISOString();
              const serialized = args
                .map((a) => {
                  try {
                    if (typeof a === "string") return a;
                    return JSON.stringify(a);
                  } catch {
                    return String(a);
                  }
                })
                .join(" ");
              window.__srConsoleLogs.push(`[${ts}] [${lvl.toUpperCase()}] ${serialized}`);
              if (window.__srConsoleLogs.length > 1000) {
                window.__srConsoleLogs.splice(0, window.__srConsoleLogs.length - 1000);
              }
            } catch {}
            try {
              orig.apply(console, args);
            } catch {}
          };
        });
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (!open) {
      // reset when closing
      setFeedbackText("");
      setFeedbackRating(null);
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      } catch {}
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      setIsRecording(false);
      setAudioBlob(null);
      setRecordingSeconds(0);
      setBaseImageUrl(null);
      setActions([]);
      setConsoleText("");
    }
  }, [open]);

  useEffect(() => {
    if (open && typeof initialText === "string") {
      setFeedbackText(initialText);
    }
  }, [open, initialText]);

  const MAX_RECORD_SECONDS = 600;
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeTypes = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/ogg",
        "audio/mp4",
      ];
      const supportedType = mimeTypes.find((t) =>
        window.MediaRecorder && MediaRecorder.isTypeSupported ? MediaRecorder.isTypeSupported(t) : true
      );
      const mr = supportedType ? new MediaRecorder(stream, { mimeType: supportedType }) : new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      mr.onstop = () => {
        const blobType =
          supportedType && supportedType.includes("ogg")
            ? "audio/ogg"
            : supportedType && supportedType.includes("mp4")
            ? "audio/mp4"
            : "audio/webm";
        const blob = new Blob(chunks, { type: blobType });
        setAudioBlob(blob);
        try {
          stream.getTracks().forEach((t) => t.stop());
        } catch {}
      };
      mediaRecorderRef.current = mr;
      setRecordingSeconds(0);
      setIsRecording(true);
      mr.start(1000);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((s) => {
          if (s + 1 >= MAX_RECORD_SECONDS) stopRecording();
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      alertDlg && alertDlg("Microphone permission denied or not available.");
      setIsRecording(false);
    }
  };
  const stopRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch {}
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
  };
  const resetRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setRecordingSeconds(0);
  };
  const toggleRecording = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  const MicControls = () => (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
      <Button
        type={isRecording ? "primary" : "default"}
        danger={isRecording}
        shape="circle"
        onClick={toggleRecording}
        style={{ width: 56, height: 56, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}
      >
        {isRecording ? <StopOutlined style={{ fontSize: 24 }} /> : <AudioOutlined style={{ fontSize: 24 }} />}
      </Button>
      <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 8, textAlign: "center" }}>
        {isRecording
          ? `${String(Math.floor(recordingSeconds / 60)).padStart(2, "0")}:${String(recordingSeconds % 60).padStart(2, "0")} / 10:00`
          : audioBlob
          ? "Recorded audio ready"
          : "Tap mic to add an audio note (max 10 min)"}
      </div>
      {audioBlob && (
        <div style={{ width: "100%" }}>
          <audio controls src={URL.createObjectURL(audioBlob)} style={{ width: "100%" }} />
          <div style={{ marginTop: 6, textAlign: "right" }}>
            <Button size="small" danger icon={<DeleteOutlined />} onClick={resetRecording}>
              Delete recording
            </Button>
          </div>
        </div>
      )}
    </div>
  );
  const canSend = useMemo(() => {
    if (isBug) {
      return Boolean(
        (feedbackText && feedbackText.trim()) || audioBlob || baseImageUrl || (consoleText && consoleText.trim())
      );
    }
    return Boolean(
      (feedbackText && feedbackText.trim()) || audioBlob || (feedbackRating !== null && feedbackRating !== undefined)
    );
  }, [isBug, feedbackText, audioBlob, feedbackRating, baseImageUrl, consoleText]);

  const drawAll = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const img = baseImgRef.current;
    if (!canvas || !ctx || !img) return;
    const w = img.clientWidth || img.naturalWidth;
    const h = img.clientHeight || img.naturalHeight;
    canvas.width = w;
    canvas.height = h;
    ctx.clearRect(0, 0, w, h);
    // draw base
    ctx.drawImage(img, 0, 0, w, h);
    // draw committed actions
    actions.forEach((a) => drawAction(ctx, a));
    // preview
    if (isDrawing) {
      if (tool === TOOL_PEN) {
        drawAction(ctx, { type: TOOL_PEN, color, thickness, points: currentPoints });
      } else if (tool === TOOL_RECT && startPoint && previewEnd) {
        drawAction(ctx, { type: TOOL_RECT, color, thickness, rect: { x1: startPoint.x, y1: startPoint.y, x2: previewEnd.x, y2: previewEnd.y } });
      } else if (tool === TOOL_ARROW && startPoint && previewEnd) {
        drawAction(ctx, { type: TOOL_ARROW, color, thickness, arrow: { x1: startPoint.x, y1: startPoint.y, x2: previewEnd.x, y2: previewEnd.y } });
      }
    }
  }, [actions, isDrawing, currentPoints, previewEnd, startPoint, color, thickness, tool]);

  const drawAction = (ctx, action) => {
    ctx.save();
    ctx.strokeStyle = action.color;
    ctx.lineWidth = action.thickness;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    if (action.type === TOOL_PEN) {
      const pts = action.points || [];
      if (pts.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) {
        ctx.lineTo(pts[i].x, pts[i].y);
      }
      ctx.stroke();
    } else if (action.type === TOOL_RECT) {
      const { x1, y1, x2, y2 } = action.rect;
      const x = Math.min(x1, x2);
      const y = Math.min(y1, y2);
      const w = Math.abs(x2 - x1);
      const h = Math.abs(y2 - y1);
      ctx.strokeRect(x, y, w, h);
    } else if (action.type === TOOL_ARROW) {
      const { x1, y1, x2, y2 } = action.arrow;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      // arrow head
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const headLen = Math.max(8, action.thickness * 3);
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
      ctx.stroke();
    }
    ctx.restore();
  };

  useEffect(() => {
    drawAll();
  }, [drawAll]);

  // Redraw on window resize to keep canvas in sync with image size
  useEffect(() => {
    const onResize = () => {
      try {
        drawAll();
      } catch {}
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [drawAll]);

  const containerRect = () => {
    const el = containerRef.current;
    const rect = el?.getBoundingClientRect();
    return rect || { left: 0, top: 0 };
  };
  const toLocalPoint = (e) => {
    const rect = containerRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const handleMouseDown = (e) => {
    if (!baseImageUrl) return;
    setIsDrawing(true);
    const p = toLocalPoint(e);
    setStartPoint(p);
    if (tool === TOOL_PEN) {
      setCurrentPoints([p]);
    }
  };
  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const p = toLocalPoint(e);
    if (tool === TOOL_PEN) {
      setCurrentPoints((pts) => [...pts, p]);
    } else {
      setPreviewEnd(p);
    }
  };
  const handleMouseUp = () => {
    if (!isDrawing) return;
    const committed = [...actions];
    if (tool === TOOL_PEN) {
      committed.push({ type: TOOL_PEN, color, thickness, points: currentPoints });
    } else if (tool === TOOL_RECT && startPoint && previewEnd) {
      committed.push({ type: TOOL_RECT, color, thickness, rect: { x1: startPoint.x, y1: startPoint.y, x2: previewEnd.x, y2: previewEnd.y } });
    } else if (tool === TOOL_ARROW && startPoint && previewEnd) {
      committed.push({ type: TOOL_ARROW, color, thickness, arrow: { x1: startPoint.x, y1: startPoint.y, x2: previewEnd.x, y2: previewEnd.y } });
    }
    setActions(committed);
    setIsDrawing(false);
    setStartPoint(null);
    setPreviewEnd(null);
    setCurrentPoints([]);
  };

  const undo = () => {
    setActions((a) => a.slice(0, Math.max(0, a.length - 1)));
  };
  const clearAll = () => {
    setActions([]);
  };

  const captureScreenshot = async (silent = false) => {
    try {
      const mod = await import(/* webpackChunkName: "html2canvas" */ "html2canvas");
      const el = document.querySelector(".app-layout") || document.body;
      const canvas = await mod.default(el, {
        useCORS: true,
        logging: false,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
        scale: Math.max(2, window.devicePixelRatio || 1),
      });
      const dataUrl = canvas.toDataURL("image/png");
      setBaseImageUrl(dataUrl);
    } catch (e) {
      if (!silent) {
        alertDlg && alertDlg("Screenshot capture not available. Try uploading or pasting an image.");
      }
    }
  };
  const onPaste = (e) => {
    try {
      const items = e.clipboardData?.items || [];
      for (const item of items) {
        if (item.type.indexOf("image") !== -1) {
          const blob = item.getAsFile();
          const reader = new FileReader();
          reader.onload = () => setBaseImageUrl(reader.result);
          reader.readAsDataURL(blob);
          break;
        }
      }
    } catch {}
  };
  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBaseImageUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const buildScreenshotBlob = async () => {
    if (!baseImageUrl) return null;
    // Ensure we have an image element with natural dimensions available
    const imgEl = await (async () => {
      const ref = baseImgRef.current;
      if (ref && ref.naturalWidth > 0) return ref;
      // Fallback: load a temporary image
      try {
        await new Promise((resolve, reject) => {
          const tmp = new Image();
          tmp.crossOrigin = "anonymous";
          tmp.onload = () => resolve(null);
          tmp.onerror = () => resolve(null);
          tmp.src = baseImageUrl;
        });
      } catch {}
      return baseImgRef.current;
    })();
    const naturalWidth = imgEl?.naturalWidth || 0;
    const naturalHeight = imgEl?.naturalHeight || 0;
    if (!naturalWidth || !naturalHeight) {
      // Fallback to original data URL blob if we cannot determine size
      return dataUrlToBlob(baseImageUrl);
    }
    const displayWidth = baseImgRef.current?.clientWidth || naturalWidth;
    const scale = displayWidth ? naturalWidth / displayWidth : 1;
    const offscreen = document.createElement("canvas");
    offscreen.width = naturalWidth;
    offscreen.height = naturalHeight;
    const ctx = offscreen.getContext("2d");
    if (!ctx) return dataUrlToBlob(baseImageUrl);
    // Draw base image at full resolution
    ctx.drawImage(imgEl, 0, 0, naturalWidth, naturalHeight);
    // Draw annotations scaled from display coordinates to natural coordinates
    ctx.save();
    ctx.scale(scale, scale);
    actions.forEach((a) => drawAction(ctx, a));
    ctx.restore();
    // Export as PNG
    try {
      return await new Promise((resolve) => {
        offscreen.toBlob((b) => resolve(b), "image/png");
      });
    } catch {
      try {
        return dataUrlToBlob(offscreen.toDataURL("image/png"));
      } catch {
        return dataUrlToBlob(baseImageUrl);
      }
    }
  };

  const handleSubmit = async () => {
    const text = (feedbackText || "").trim();
    if (!canSend) {
      alertDlg && alertDlg(isBug ? "Please provide details, screenshot, logs, or audio. (French or English preferred)" : "Please provide text, rating, or an audio note. (French or English preferred)");
      return;
    }
    try {
      setIsSubmitting(true);
      const formData = new FormData();
      let finalText = text;
      if (isBug) {
        finalText = `[BUG REPORT]\n${text}`;
        if (consoleText && consoleText.trim()) {
          finalText += `\n\nConsole Log:\n${consoleText.trim()}`;
        }
      }
      if (finalText) formData.append("feedback", finalText);
      if (!isBug && feedbackRating !== null && feedbackRating !== undefined) {
        formData.append("rating", feedbackRating);
      }
      if (audioBlob) {
        const inferredExt =
          audioBlob.type.includes("webm") ? "webm" :
          audioBlob.type.includes("ogg") ? "ogg" :
          audioBlob.type.includes("mp3") || audioBlob.type.includes("mpeg") ? "mp3" :
          audioBlob.type.includes("wav") ? "wav" : "dat";
        formData.append("audio", audioBlob, `audio_${Date.now()}.${inferredExt}`);
        formData.append("audio_duration_sec", Math.min(Math.round(recordingSeconds), 600).toString());
      }
      if (isBug && baseImageUrl) {
        const shot = await buildScreenshotBlob();
        if (shot) formData.append("screenshot", shot, `screenshot_${Date.now()}.png`);
      }
      await axiosInstance.post("/send_feedback/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alertDlg && alertDlg(isBug ? "Bug report sent. Thank you!" : "Feedback well sent");
      onClose && onClose();
    } catch (e) {
      alertDlg && alertDlg("Error when submitting. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-prepare bug report context on open (default click on screenshot + logs)
  useEffect(() => {
    if (open && isBug) {
      if (initialScreenshot) {
        setBaseImageUrl(initialScreenshot);
      }
      // Try to capture a fresh screenshot silently
      captureScreenshot(true);
      // Preload logs (last 100 lines)
      try {
        const logs = (window.__srConsoleLogs || []).slice(-100).join("\n");
        setConsoleText(logs || "");
      } catch {
        setConsoleText(consoleText || "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isBug]);

  return (
    <Modal
      title={isBug ? "Report a Bug" : "Send Feedback"}
      open={open}
      style={{ zIndex: 100000 }}
      rootClassName="liquid-glass-modal"
      onCancel={onClose}
      width={isBug ? "95vw" : undefined}
      styles={{ body: { maxHeight: "calc(80vh - 160px)", overflow: "auto" } }}
      footer={[
        <Button key="back" onClick={onClose}>Back</Button>,
        <Button key="send" type="primary" className="primary-glass-btn" onClick={handleSubmit} disabled={!canSend || isSubmitting} loading={isSubmitting}>
          Send
        </Button>
      ]}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} onPaste={isBug ? onPaste : undefined}>
        {!isBug && (
          <Form.Item label="How do you like the app ?">
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Rate allowHalf={true} onChange={(v) => setFeedbackRating(v)} />
            </div>
          </Form.Item>
        )}
        <Form.Item label={isBug ? "Describe the problem (French or English preferred)" : "Your message (French or English preferred)"}>
          {isBug ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "stretch" }}>
              <div>
                <Input.TextArea
                  rows={4}
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder={isBug ? "Describe what happened, including steps to reproduce and what you expected vs. what actually occurred. For trainer or heart rate sensor issues, please include the device brand and model." : "Share your thoughts and let us know if you'd like to be contacted regarding your feedback."}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <MicControls />
              </div>
            </div>
          ) : (
            <div style={{ maxWidth: 760, margin: "0 auto" }}>
              <div>
                <Input.TextArea
                  rows={4}
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Share your thoughts and let us know if you'd like to be contacted regarding your feedback."
                />
              </div>
              <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
                <div style={{ width: 280 }}><MicControls /></div>
              </div>
            </div>
          )}
        </Form.Item>

        {isBug && (
          <>
            <div style={{ marginBottom: 8 }}>
              <Space wrap>
                <Tooltip title="Capture current view">
                  <Button icon={<CameraOutlined />} onClick={captureScreenshot}>Capture</Button>
                </Tooltip>
                <input type="file" accept="image/*" onChange={onUpload} />
                <Segmented
                  options={[
                    { label: <span><EditOutlined /> Pen</span>, value: TOOL_PEN },
                    { label: <span><BorderOutlined /> Rect</span>, value: TOOL_RECT },
                    { label: <span><ColumnWidthOutlined /> Arrow</span>, value: TOOL_ARROW },
                  ]}
                  value={tool}
                  onChange={setTool}
                />
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
                <Segmented
                  options={[1, 2, 3, 4, 6, 8].map((v) => ({ label: `${v}px`, value: v }))}
                  value={thickness}
                  onChange={setThickness}
                />
                <Button icon={<UndoOutlined />} onClick={undo}>Undo</Button>
                <Button icon={<ClearOutlined />} onClick={clearAll}>Clear</Button>
              </Space>
            </div>
            <div
              ref={containerRef}
              style={{
                position: "relative",
                width: "100%",
                minHeight: 400,
                border: "1px dashed rgba(0,0,0,0.15)",
                borderRadius: 8,
                overflow: "hidden",
                marginBottom: 12,
              }}
            >
              {baseImageUrl ? (
                <>
                  <img
                    ref={baseImgRef}
                    src={baseImageUrl}
                    alt="screenshot"
                    style={{ display: "block", width: "100%", height: "auto", userSelect: "none", pointerEvents: "none" }}
                    onLoad={() => drawAll()}
                  />
                  <canvas
                    ref={canvasRef}
                    style={{ position: "absolute", inset: 0, cursor: tool === TOOL_PEN ? "crosshair" : "default" }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                  />
                </>
              ) : (
                <div style={{ padding: 16, color: "rgba(0,0,0,0.55)", textAlign: "center" }}>
                  Click Capture, upload an image, or paste a screenshot here.
                </div>
              )}
            </div>

            <Form.Item label="Console log (auto-captured, you can edit)">
              <Input.TextArea
                rows={6}
                value={consoleText}
                onChange={(e) => setConsoleText(e.target.value)}
                style={{ whiteSpace: "pre", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}
                placeholder="Console output will appear here. You can also paste logs manually."
              />
              <div style={{ marginTop: 8 }}>
                <Space>
                  <Button
                    onClick={() => {
                      try {
                        const logs = (window.__srConsoleLogs || []).slice(-50).join("\n");
                        setConsoleText(logs || "");
                      } catch {
                        setConsoleText(consoleText || "");
                      }
                    }}
                  >
                    Reload last 50 lines
                  </Button>
                  <Button
                    onClick={() => {
                      try {
                        const logs = (window.__srConsoleLogs || []).slice(-100).join("\n");
                        setConsoleText(logs || "");
                      } catch {
                        setConsoleText(consoleText || "");
                      }
                    }}
                  >
                    Load last 100 lines
                  </Button>
                </Space>
              </div>
            </Form.Item>
          </>
        )}

        <p>Or email to <a href="mailto:sam@simplyride.app" target="_blank" rel="noreferrer">sam@simplyride.app</a>.</p>
      </Form>
    </Modal>
  );
}


